import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useInView } from "react-intersection-observer";
import "./Artwork.css";

const artworks = [
  {
    name: "Black Widow Tincture",
    image: "art/blackwidowtincture.webp",
    description: "A deadly poison crafted by master alchemists",
  },
  {
    name: "Bloodthrill",
    image: "art/bloodthrill.webp",
    description: "The intoxicating rush of combat",
  },
  {
    name: "Fatal Thrust",
    image: "art/fatalthrust.webp",
    description: "A precise strike that ends the battle",
  },
  {
    name: "Loramere Waterskin",
    image: "art/loramerewaterskin.webp",
    description: "Mystical waters that restore the spirit",
  },
  {
    name: "Sap",
    image: "art/Sap.webp",
    description: "Drain your enemy's strength and vitality",
  },
  {
    name: "Scorching Ray",
    image: "art/scorchingray.webp",
    description: "A beam of concentrated fire magic",
  },
  {
    name: "Spellshock",
    image: "art/spellshock.webp",
    description: "Overwhelming magical energy disrupts the mind",
  },
];

interface ArtworkProps {
  onEarlyAccessClick?: () => void;
}

const Artwork: React.FC<ArtworkProps> = ({ onEarlyAccessClick }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.3,
  });

  const nextArtwork = () => {
    setCurrentIndex((prev) => (prev + 1) % artworks.length);
  };

  const prevArtwork = () => {
    setCurrentIndex((prev) => (prev - 1 + artworks.length) % artworks.length);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
      },
    },
  };

  return (
    <section id="artwork" className="artwork-section" ref={ref}>
      <div className="container">
        <motion.div
          className="artwork-content"
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          {/* Header */}
          <motion.div className="artwork-header" variants={itemVariants}>
            <h2>Handcrafted Artwork</h2>
            <p>
              Every image in Seekers: Shards of Imriel is created by hand with
              absolutely no AI. We have collaborated with over thirty talented
              artists from around the world, each bringing their own vision and
              mastery to the realm of Imriel. As funding grows, our goal is to
              push for the highest possible quality by working with the best
              creative minds to craft a game that is not only worth playing but
              also worth collecting.
            </p>
          </motion.div>

          {/* Artwork Carousel */}
          <motion.div className="artwork-carousel" variants={itemVariants}>
            <div className="carousel-container">
              {/* Navigation Buttons */}
              <button className="carousel-nav prev" onClick={prevArtwork}>
                <span>‹</span>
              </button>

              <button className="carousel-nav next" onClick={nextArtwork}>
                <span>›</span>
              </button>

              {/* Main Display */}
              <div className="artwork-display">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentIndex}
                    className="artwork-main"
                    initial={{ opacity: 0, x: 100 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -100 }}
                    transition={{
                      duration: 0.3,
                      ease: "easeInOut",
                    }}
                  >
                    <div className="artwork-frame">
                      <img
                        src={artworks[currentIndex].image}
                        alt={artworks[currentIndex].name}
                      />
                      <div className="artwork-glow"></div>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Thumbnails */}
              <div className="artwork-thumbnails">
                {artworks.map((artwork, index) => (
                  <motion.button
                    key={artwork.name}
                    className={`thumbnail ${
                      index === currentIndex ? "active" : ""
                    }`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setCurrentIndex(index);
                    }}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <img src={artwork.image} alt={artwork.name} />
                  </motion.button>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Call to Action */}
          <motion.div className="artwork-cta" variants={itemVariants}>
            <p className="cta-text">
              experience the full collection
            </p>
            <button 
              className="btn cta-btn"
              onClick={onEarlyAccessClick}
            >
              JOIN
            </button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Artwork;
